import { useState } from 'react';
import { navigate } from '../app/router';
import { useAppData } from '../app/AppProvider';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { LineDots } from '../components/LineDots';
import { TopBar } from '../components/TopBar';
import { METRO_STATION_BY_ID } from '../data/metro';
import { RETURN_INTENT_LABELS, STATUS_LABELS } from '../domain/labels';

const dateFormatter = new Intl.DateTimeFormat('ru', { dateStyle: 'long', timeStyle: 'short' });

export function PlacePage({ placeId }: { placeId: string }) {
  const { data, deletePlace } = useAppData();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const place = data.places.find((item) => item.id === placeId);

  if (!place) {
    return (
      <main className="page">
        <TopBar title="Место не найдено" />
        <EmptyState title="Такого места нет">Возможно, оно было удалено или резервная копия изменила данные.</EmptyState>
      </main>
    );
  }

  const station = METRO_STATION_BY_ID.get(place.stationId);
  const backHref = station ? `#/station/${encodeURIComponent(station.id)}` : '#/';

  const remove = async () => {
    if (!window.confirm(`Удалить «${place.name}»? Это действие нельзя отменить.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await deletePlace(place.id);
      navigate(backHref, true);
    } catch (cause) {
      console.error(cause);
      setError('Не удалось удалить место. Попробуйте ещё раз.');
      setDeleting(false);
    }
  };

  return (
    <main className="page">
      <TopBar title="Место" backHref={backHref} right={
        <a className="icon-button icon-button--plain" href={`#/place/${encodeURIComponent(place.id)}/edit`} aria-label="Редактировать место"><Icon name="edit" /></a>
      } />

      <article className="detail-card">
        <header className="detail-header">
          <h1>{place.name}</h1>
          <div className="detail-badges">
            <span className={`status-badge ${place.status === 'visited' ? 'status-badge--visited' : ''}`}>{STATUS_LABELS[place.status]}</span>
            {place.returnIntent ? <span className="intent-badge">{RETURN_INTENT_LABELS[place.returnIntent]}</span> : null}
          </div>
        </header>

        <div className="detail-row">
          <p className="detail-label">Станция метро</p>
          {station ? (
            <p className="detail-value"><a className="detail-link" href={backHref}>{station.name}</a> <LineDots lines={station.lines} /></p>
          ) : <p className="detail-value">Станция отсутствует в справочнике</p>}
        </div>

        <div className="detail-row">
          <p className="detail-label">Намерение вернуться</p>
          <p className="detail-value">
            {place.status === 'want' ? 'Не применимо' : place.returnIntent ? RETURN_INTENT_LABELS[place.returnIntent] : 'Не указано'}
          </p>
        </div>

        <div className="detail-row">
          <p className="detail-label">Теги</p>
          {place.tags.length > 0 ? <div className="tags">{place.tags.map((tag) => <span className="tag-chip" key={tag}>{tag}</span>)}</div> : <p className="detail-value muted">Не указаны</p>}
        </div>

        <div className="detail-row">
          <p className="detail-label">Комментарий</p>
          <p className="detail-value">{place.comment || 'Не указан'}</p>
        </div>

        <div className="detail-row">
          <p className="detail-label">Ссылка на Яндекс Карты</p>
          <p className="detail-value"><a className="detail-link" href={place.yandexMapsUrl} target="_blank" rel="noreferrer">{place.yandexMapsUrl}</a></p>
        </div>

        <div className="detail-row">
          <p className="detail-label">Даты</p>
          <p className="detail-value">Добавлено: {dateFormatter.format(new Date(place.createdAt))}<br />Изменено: {dateFormatter.format(new Date(place.updatedAt))}</p>
        </div>
      </article>

      {error ? <p className="notice notice--danger" role="alert">{error}</p> : null}
      <div className="detail-actions" style={{ marginTop: 14 }}>
        <a className="button" href={place.yandexMapsUrl} target="_blank" rel="noreferrer"><Icon name="route" /> Построить маршрут</a>
        <a className="button button--secondary" href={`#/place/${encodeURIComponent(place.id)}/edit`}><Icon name="edit" /> Редактировать</a>
        <button className="button button--danger" type="button" disabled={deleting} onClick={() => void remove()}><Icon name="trash" /> {deleting ? 'Удаление…' : 'Удалить место'}</button>
      </div>
    </main>
  );
}
